import { useCallback, useEffect, useRef, useState } from 'react';
import { ActionIcon, Group, Loader, Text, Tooltip } from '@mantine/core';
import { InlineIcon } from '@iconify/react';
import { showNotification } from '@mantine/notifications';
import { AudioCapture } from 'lib/whisper/capture';
import { trimSilence } from 'lib/whisper/silence';
import { useWhisper } from 'lib/whisper/useWhisper';
import { Waveform } from './Waveform';

type UIState = 'idle' | 'preparing' | 'recording' | 'transcribing';

interface Props {
  disabled?: boolean;
  onTranscript: (text: string) => void;
  onStateChange?: (isActive: boolean) => void;
}

export const VoiceInput = ({
  disabled,
  onTranscript,
  onStateChange,
}: Props) => {
  const [state, setState] = useState<UIState>('idle');
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const [elapsedS, setElapsedS] = useState(0);
  const [maxDurationS, setMaxDurationS] = useState(0);
  const captureRef = useRef<AudioCapture | null>(null);
  const tickRef = useRef<number>(undefined);
  const whisper = useWhisper();

  const updateState = useCallback(
    (next: UIState) => {
      setState(next);
      onStateChange?.(next !== 'idle');
    },
    [onStateChange]
  );

  useEffect(
    () => () => {
      captureRef.current?.dispose();
      captureRef.current = null;
    },
    []
  );

  const cleanup = useCallback(() => {
    if (tickRef.current) {
      window.clearInterval(tickRef.current);
      tickRef.current = undefined;
    }
    captureRef.current?.dispose();
    captureRef.current = null;
    setAnalyser(null);
    setElapsedS(0);
  }, []);

  const handleConfirmRef = useRef<() => void>(() => {});

  const handleStart = useCallback(async () => {
    if (disabled) return;
    updateState('preparing');
    try {
      const capture = new AudioCapture({
        onLimitReached: () => handleConfirmRef.current(),
      });
      captureRef.current = capture;
      const modelReady = whisper.load();
      await capture.requestMic();
      await modelReady;

      setAnalyser(capture.analyser);
      setMaxDurationS(capture.maxDurationS);
      capture.startRecording();
      tickRef.current = window.setInterval(() => {
        setElapsedS(Math.floor(capture.recordedDurationS));
      }, 250);
      updateState('recording');
    } catch (err) {
      cleanup();
      updateState('idle');
      const message = err instanceof Error ? err.message : String(err);
      showNotification({
        message: `Voice input failed: ${message}`,
        color: 'red',
      });
    }
  }, [disabled, whisper, cleanup, updateState]);

  const handleCancel = useCallback(() => {
    cleanup();
    updateState('idle');
  }, [cleanup, updateState]);

  const handleConfirm = useCallback(async () => {
    const capture = captureRef.current;
    if (!capture) return;
    if (tickRef.current) {
      window.clearInterval(tickRef.current);
      tickRef.current = undefined;
    }
    capture.stopRecording();
    const pcm = capture.getRecordedAudio();
    // Release the mic and tear down the audio graph before transcription —
    // no further audio can be captured once the user has hit send.
    capture.dispose();
    captureRef.current = null;
    setAnalyser(null);

    const speech = trimSilence(pcm);
    if (!speech) {
      cleanup();
      updateState('idle');
      showNotification({
        message: "Didn't catch any speech — try again.",
        color: 'gray',
      });
      return;
    }

    updateState('transcribing');
    try {
      const text = await whisper.transcribe(speech);
      if (text) onTranscript(text);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      showNotification({
        message: `Transcription failed: ${message}`,
        color: 'red',
      });
    } finally {
      cleanup();
      updateState('idle');
    }
  }, [whisper, onTranscript, cleanup, updateState]);

  useEffect(() => {
    handleConfirmRef.current = handleConfirm;
  }, [handleConfirm]);

  if (state === 'idle') {
    return (
      <Tooltip label="Record voice message" withinPortal>
        <ActionIcon
          size="lg"
          variant="light"
          color="teal"
          onClick={handleStart}
          disabled={disabled}>
          <InlineIcon icon="tabler:microphone" height={20} />
        </ActionIcon>
      </Tooltip>
    );
  }

  if (state === 'preparing') {
    const isFirstLoad = !whisper.isReady;
    return (
      <Group gap="md" sx={{ flex: 1, minWidth: 0 }} wrap="nowrap">
        <Loader size="xs" />
        <Text size="xs" c="dimmed" lineClamp={1}>
          {isFirstLoad
            ? 'Preparing voice model for the first time…'
            : 'Starting microphone…'}
        </Text>
      </Group>
    );
  }

  if (state === 'transcribing') {
    return (
      <Group gap="md" sx={{ flex: 1, minWidth: 0 }} wrap="nowrap">
        <Loader size="xs" />
        <Text size="xs" c="dimmed" lineClamp={1} sx={{ flex: 1 }}>
          Transcribing…
        </Text>
        <ActionIcon size="lg" color="teal" variant="filled" loading disabled>
          <InlineIcon icon="fluent:send-28-regular" height={20} />
        </ActionIcon>
      </Group>
    );
  }

  const remainingS = Math.max(0, maxDurationS - elapsedS);
  const nearLimit = remainingS <= 30;

  return (
    <Group gap="md" sx={{ flex: 1, minWidth: 0 }} wrap="nowrap">
      <Tooltip label="Cancel" withinPortal>
        <ActionIcon
          size="lg"
          variant="subtle"
          color="red"
          onClick={handleCancel}>
          <InlineIcon icon="tabler:x" height={20} />
        </ActionIcon>
      </Tooltip>
      <Waveform analyser={analyser} />
      <Text
        size="xs"
        c={nearLimit ? 'orange' : 'dimmed'}
        fw={nearLimit ? 600 : 400}
        sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        {formatDuration(elapsedS)} / {formatDuration(maxDurationS)}
      </Text>
      <ActionIcon
        size="lg"
        color="teal"
        variant="filled"
        onClick={handleConfirm}>
        <InlineIcon icon="fluent:send-28-regular" height={20} />
      </ActionIcon>
    </Group>
  );
};

function formatDuration(totalS: number): string {
  const m = Math.floor(totalS / 60);
  const s = totalS % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
